import React, { useEffect, useRef, useState } from 'react';
import { playsInDrivePlayer } from '../../lib/driveMedia';

const TILE_SIZES = '(max-width: 600px) 50vw, 220px';

// One shared observer plays loop clips only while they are on screen
let loopObserver = null;
const getLoopObserver = () => {
  if (!loopObserver && 'IntersectionObserver' in window) {
    loopObserver = new IntersectionObserver(
      entries => entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) target.play().catch(() => {});
        else target.pause();
      }),
      { rootMargin: '200px 0px' }
    );
  }
  return loopObserver;
};

const LoopVideo = ({ photo }) => {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    const observer = getLoopObserver();
    if (!observer) {
      video.play().catch(() => {});
      return undefined;
    }
    observer.observe(video);
    return () => observer.unobserve(video);
  }, []);

  return (
    <video
      ref={ref}
      className="gallery-media"
      src={photo.file}
      poster={photo.thumb}
      muted
      loop
      playsInline
      preload="none"
    />
  );
};

const Media = ({ photo }) => {
  const [broken, setBroken] = useState(false);
  const isVideoFile = /^video\//.test(photo.mimeType || '');

  if (playsInDrivePlayer(photo)) {
    return (
      <React.Fragment>
        {broken
          ? <div className="gallery-video-fallback">{photo.name}</div>
          : <img className="gallery-media" src={photo.thumb} srcSet={photo.thumbSet} sizes={TILE_SIZES} alt={photo.name} loading="lazy" onError={() => setBroken(true)} />}
        <span className="gallery-play" aria-hidden="true">▶</span>
      </React.Fragment>
    );
  }

  if (photo.type === 'gif' && isVideoFile) return <LoopVideo photo={photo} />;

  // Real GIFs need the original file to animate
  if (photo.mimeType === 'image/gif') {
    return <img className="gallery-media" src={photo.file} alt={photo.name} loading="lazy" />;
  }

  return <img className="gallery-media" src={photo.thumb} srcSet={photo.thumbSet} sizes={TILE_SIZES} alt={photo.name} loading="lazy" />;
};

const Item = ({ photo, onPhotoClick }) => (
  <div
    className="gallery-item"
    role="button"
    tabIndex={0}
    aria-label={`Open ${photo.name}`}
    onClick={() => onPhotoClick(photo.id)}
    onKeyDown={event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onPhotoClick(photo.id);
      }
    }}
  >
    <Media photo={photo} />
  </div>
);

export default Item;
